import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  PhonenumberMonthlyCostParentEditForm,
  PhonenumberMonthlyCostParentEditInitialValues,
  PhonenumberMonthlyCostParentComponents,
  PhonenumberMonthlyCostParentAdditionalParams,
  PhonenumberMonthlyCostParentFormOptions,
  onPhonenumberMonthlyCostParentFormSaved,
  IPhonenumberMonthlyCostParentFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedPhonenumberMonthlyCostParent, ISharedPatchPhonenumberMonthlyCostParent, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchPhonenumberMonthlyCostParent, patchPhonenumberMonthlyCostParent, getPhonenumberMonthlyCostParentUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedPhonenumberMonthlyCostParent> {}

const PhonenumberMonthlyCostParentFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchPhonenumberMonthlyCostParent(id).then(phonenumber_monthly_cost_parent => setValue(phonenumber_monthly_cost_parent));
  }, [id, setValue, ]);

  const param_props: IPhonenumberMonthlyCostParentFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && PhonenumberMonthlyCostParentEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && PhonenumberMonthlyCostParentFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchPhonenumberMonthlyCostParent, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchPhonenumberMonthlyCostParent) => value && save(() => patchPhonenumberMonthlyCostParent(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onPhonenumberMonthlyCostParentFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(null, CrudEnum.Update, PermissionEnum.PhonenumberMonthlyCostParent, value)}
    >
      <AtlasForm<ISharedPatchPhonenumberMonthlyCostParent>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={PhonenumberMonthlyCostParentComponents}
        additionalParams={PhonenumberMonthlyCostParentAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={PhonenumberMonthlyCostParentEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('PHONENUMBER_MONTHLY_COST_PARENT')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PhonenumberMonthlyCostParentFormEditView = React.memo(withEdit(PhonenumberMonthlyCostParentFormEdit, getPhonenumberMonthlyCostParentUpdateSchema));
